import React, { useEffect, useMemo, useState } from "react";
import { DialogContainer } from "react-md";
import { RiCloseLine } from "react-icons/ri";
import successGif from "../../../../../../assets/images/done-animation.gif";
import "../../styles/navigoDashboardStyles.css";
import "./CancelAppointmentFlow.css";
import { REFUND_STATUS } from "../../helper/appointmentConstants";
import { formatCurrency } from "../../helper/formatCurrency";
import EventService from "../../../../../../service/event/EventService";
import NavigoDashboardUtils from "../../services/NavigoDashboardUtils";

// Extra chip (not returned by the API) that reveals a free-text comment.
const OTHER_REASON = "Other reason";
// refundRequest values sent with the status update.
const REFUND_REQUEST = Object.freeze({
  WITH_REFUND: 2,
  WITHOUT_REFUND: 3,
  FORCED_REFUND: 5,
});

const STEP = Object.freeze({
  CONFIRM: 1,
  REFUND: 2,
  CANCELLED: 3,
  REFUNDED: 4,
});

/**
 * The restyled cancel flow, used only when the chosen status is "Cancelled".
 * Every other status (no show / declined / not eligible) still goes through
 * CancelAppointmentDialog + RefundDialog.
 *
 * Which variant of the confirm step is shown depends on appointment.refundStatus:
 *   2 (ELIGIBLE_APPLICABLE)     → refund offered, "Cancel with refund" emphasised
 *   3 (ELIGIBLE_NOT_APPLICABLE) → refund possible but discouraged, "Cancel
 *                                 without refund" emphasised, and the refund
 *                                 step is a forced refund
 *   anything else               → plain cancel, no refund step at all
 */
export function CancelAppointmentFlow({
  appointment,
  statusToBeApplied,
  onClose,
  onCompleted,
}) {
  const [step, setStep] = useState(STEP.CONFIRM);
  const [reasons, setReasons] = useState([]);
  const [reasonSelected, setReasonSelected] = useState(null);
  const [otherReasonComment, setOtherReasonComment] = useState("");
  const [typed, setTyped] = useState("");
  const [loading, setLoading] = useState(false);

  const navigoDashboardUtils = useMemo(() => new NavigoDashboardUtils(), []);

  const isRefundApplicable =
    appointment?.refundStatus === REFUND_STATUS.ELIGIBLE_APPLICABLE;
  const isRefundNotApplicable =
    appointment?.refundStatus === REFUND_STATUS.ELIGIBLE_NOT_APPLICABLE;
  const hasRefundChoice = isRefundApplicable || isRefundNotApplicable;

  useEffect(() => {
    const onResponse = (res) => setReasons(Array.isArray(res) ? res : []);
    const onError = (err) => {
      setReasons([]);
      EventService.emit("showError", { message: `Error: ${err?.message}` });
    };
    navigoDashboardUtils.getCancellationReasons(onResponse, onError);
  }, [navigoDashboardUtils]);

  // The chosen chip, or the free text behind "Other reason".
  const cancellationReason = () =>
    reasonSelected === OTHER_REASON
      ? otherReasonComment.trim()
      : (reasonSelected ?? "");

  const updateStatus = (body, nextStep) => {
    setLoading(true);
    const onResponse = () => {
      setLoading(false);
      setStep(nextStep);
    };
    const onError = (err) => {
      setLoading(false);
      EventService.emit("showError", { message: `Error: ${err?.message}` });
    };
    navigoDashboardUtils.handleStatusUpdate(
      { ...body, reason: cancellationReason() },
      onResponse,
      onError,
    );
  };

  // A reason has to be picked before the appointment can be cancelled.
  const hasReason = () => {
    if (reasonSelected == null) {
      EventService.emit("showError", {
        message: `Please select a reason for cancellation`,
      });
      return false;
    }
    if (reasonSelected === OTHER_REASON && otherReasonComment.trim() === "") {
      EventService.emit("showError", {
        message: `Please add a comment for the other reason`,
      });
      return false;
    }
    return true;
  };

  const cancelWithoutRefund = () => {
    if (!hasReason()) return;
    updateStatus(
      hasRefundChoice
        ? {
            appointmentID: appointment?.id,
            status: statusToBeApplied,
            refundRequest: REFUND_REQUEST.WITHOUT_REFUND,
          }
        : {
            appointmentID: appointment?.id,
            reportUrl: "",
            status: statusToBeApplied,
          },
      STEP.CANCELLED,
    );
  };

  const initiateRefund = () => {
    updateStatus(
      {
        appointmentID: appointment?.id,
        status: statusToBeApplied,
        // A refund on a "not applicable" appointment is a forced one.
        refundRequest: isRefundNotApplicable
          ? REFUND_REQUEST.FORCED_REFUND
          : REFUND_REQUEST.WITH_REFUND,
      },
      STEP.REFUNDED,
    );
  };

  const handleDone = () => {
    if (onCompleted) onCompleted();
    onClose();
  };

  if (appointment == null) return <></>;

  const header = (title) => (
    <>
      <div className="caf-header">
        <span className="caf-title">{title}</span>
        <button type="button" className="caf-close-btn" onClick={onClose}>
          <RiCloseLine style={{ fontSize: "20px" }} />
        </button>
      </div>
      <div className="caf-divider-full" />
    </>
  );

  const dialog = (content, width) => (
    <div className="custom-dialog-overlay">
      <DialogContainer
        id="cancel-appointment-flow-dialog"
        visible={true}
        onHide={() => {}}
        focusOnMount={false}
        autosizeContent
        width={width}
        dialogStyle={{
          borderRadius: "16px",
          position: "relative",
          zIndex: 101,
        }}
        contentStyle={{ padding: "22px 24px" }}
      >
        <div className="caf-dialog">{content}</div>
      </DialogContainer>
    </div>
  );

  if (step === STEP.CONFIRM) {
    return dialog(
      <>
        {header("Cancel appointment?")}

        <div className="caf-question">
          {`Are you sure you want to cancel this appointment?`}
        </div>
        {isRefundApplicable && (
          <div className="caf-refund-note">
            {`Rs ${appointment?.price} Refund is applicable upon cancellation of this appointment.`}
          </div>
        )}
        {isRefundNotApplicable && (
          <div className="caf-refund-note">
            Refund is <b>NOT</b> applicable as appointment is cancelled within
            24 hours of scheduled time.
          </div>
        )}
        <div className="caf-divider-inset" />

        <div className="caf-subheading">
          {`Please add a reason below`}
          <span style={{ color: "#E44437" }}>{` *`}</span>
        </div>
        <div className="caf-reason-list">
          {[...reasons, OTHER_REASON].map((reason) => (
            <button
              key={reason}
              type="button"
              className={`caf-reason-chip${
                reasonSelected === reason ? " caf-selected" : ""
              }`}
              onClick={() => {
                setReasonSelected(reason);
                if (reason !== OTHER_REASON) {
                  setOtherReasonComment("");
                }
              }}
            >
              {reason}
            </button>
          ))}
        </div>
        {reasonSelected === OTHER_REASON && (
          <textarea
            className="caf-reason-comment"
            placeholder="Add a comment…"
            value={otherReasonComment}
            onChange={(e) => setOtherReasonComment(e.target.value)}
          />
        )}

        {hasRefundChoice ? (
          <div className="caf-footer">
            <button
              type="button"
              disabled={loading}
              // On a not-applicable refund, cancelling without one is the
              // expected path, so it carries the emphasis instead.
              className={`caf-btn caf-btn-14 ${
                isRefundNotApplicable ? "caf-btn-primary" : "caf-btn-secondary"
              }`}
              onClick={cancelWithoutRefund}
            >
              {"Cancel without refund"}
            </button>
            <button
              type="button"
              disabled={loading}
              className={`caf-btn caf-btn-14 ${
                isRefundNotApplicable ? "caf-btn-secondary" : "caf-btn-primary"
              }`}
              onClick={() => {
                if (!hasReason()) return;
                setStep(STEP.REFUND);
              }}
            >
              {"Cancel with refund"}
            </button>
          </div>
        ) : (
          <div className="caf-footer">
            <button
              type="button"
              className="caf-btn caf-btn-13 caf-btn-secondary"
              onClick={onClose}
            >
              {"No, go back"}
            </button>
            <button
              type="button"
              disabled={loading}
              className="caf-btn caf-btn-13 caf-btn-primary"
              onClick={cancelWithoutRefund}
            >
              {"Yes, cancel"}
            </button>
          </div>
        )}
      </>,
      "480px",
    );
  }

  if (step === STEP.REFUND) {
    const canInitiate = typed === "REFUND" && !loading;
    return dialog(
      <>
        {header(
          isRefundNotApplicable ? "Refund is NOT applicable" : "Refund details",
        )}

        <div className="caf-payment-heading">{`Payment summary:`}</div>
        <ul className="caf-payment-list">
          {appointment?.paymentInfo?.walletPayment?.isApplicable && (
            <li>
              {`${formatCurrency(
                appointment?.paymentInfo?.walletPayment?.amount,
              )} paid via Zyla wallet`}
            </li>
          )}
          {appointment?.paymentInfo?.externalPayment?.isApplicable && (
            <li>
              {`${formatCurrency(
                appointment?.paymentInfo?.externalPayment?.amount,
              )} paid via other payment method`}
            </li>
          )}
          <li>
            {`Total: ${formatCurrency(appointment?.paymentInfo?.amount)}`}
          </li>
        </ul>

        <div className="caf-refund-instruction">
          To initiate the refund, type <b>“REFUND”</b> in the box below, then
          tap <b>“Initiate Refund”</b>
        </div>
        <input
          className="caf-refund-input"
          value={typed}
          placeholder="Type here"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="characters"
          spellCheck={false}
          onChange={(e) =>
            setTyped(e.target.value.replace(/[^a-zA-Z]/g, "").toUpperCase())
          }
        />

        <div className="caf-footer">
          <button
            type="button"
            disabled={loading}
            className="caf-btn caf-btn-13 caf-btn-secondary"
            // Back to the confirm step with the chosen reason still in place.
            onClick={() => {
              setTyped("");
              setStep(STEP.CONFIRM);
            }}
          >
            {"Cancel"}
          </button>
          <button
            type="button"
            disabled={!canInitiate}
            className={`caf-btn caf-btn-13 ${
              canInitiate ? "caf-btn-primary" : "caf-btn-disabled"
            }`}
            onClick={initiateRefund}
          >
            {"Initiate refund"}
          </button>
        </div>
      </>,
      "480px",
    );
  }

  return dialog(
    <div className="caf-success">
      <img src={successGif} alt="success" className="caf-success-img" />
      {step === STEP.REFUNDED ? (
        <>
          <div className="caf-success-refund-heading">
            {`Refund Initiated successfully`}
          </div>
          <div className="caf-success-note">
            {`Refund will be processed to the original payment sources within 7 working days.`}
          </div>
        </>
      ) : (
        <div className="caf-success-cancelled-heading">
          {`Appointment cancelled successfully`}
        </div>
      )}
      <button type="button" className="caf-done-btn" onClick={handleDone}>
        {"Done"}
      </button>
    </div>,
    "400px",
  );
}

export default CancelAppointmentFlow;
